/* eslint-disable arrow-body-style */
import React, {
  isValidElement,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { Radio } from 'tea-component';
import { useLocal, useMultiAiSummary } from '@/pages/dashboard/utils/customHook';
import { ChangeAnalysisAiSummarySection } from '../ChangeAnalysisAiSummarySection';
import {
  IAiTableData,
  IChangeAnalysisAiTableProps,
  IRegisteredTableInfo,
  ITrendTableColumnSwitchConfig,
} from '@/pages/dashboard/pages/changeAnalysis/components/ChangeAnalysisAiTable/type';
import MetricsCardWrap from '@/pages/dashboard/components/MetricsCardWrap';
import classNames from 'classnames';
import './index.scss';
import { convertCommonTableToDownloadConfig, downloadExcel } from '@/pages/dashboard/utils/download';
import { getName } from '@home/utils/common';
import { DownloadButton } from '@/pages/dashboard/components/Plugin/DownloadButton';

function applyTrendTableYoyColumn(
  columns: unknown[] | undefined,
  switchConfig: ITrendTableColumnSwitchConfig | undefined,
  alignmentValue: string,
): unknown[] | undefined {
  if (!switchConfig || !Array.isArray(columns)) {
    return columns;
  }
  const override =
    switchConfig[alignmentValue as 'version_aligned' | 'natural_day_aligned'];
  if (!override) {
    return columns;
  }
  const targetCode = switchConfig.targetColumnCode;
  return columns.map((col) => {
    const column = col as Record<string, unknown>;
    if (column?.code !== targetCode) {
      return col;
    }
    return {
      ...column,
      name: override.nameCn ?? override.nameEn ?? column.name,
      nameEn: override.nameEn ?? override.nameCn ?? column.nameEn,
      nameCn: override.nameCn ?? override.nameEn ?? column.nameCn,
      code: override.code ?? '',
    };
  });
}

interface IChangeAnalysisAiTableChildProps {
  config?: {
    props?: {
      dataKey?: unknown;
      metricsCardWrapProps?: {
        titleEn?: string;
        titleCn?: string;
      };
      commonBaseTableProps?: Record<string, unknown>;
    };
    name?: string;
  };
  [key: string]: unknown;
}

const isRegisteredTableInfo = (
  value: unknown,
): value is IRegisteredTableInfo => {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const table = value as Partial<IRegisteredTableInfo>;
  return (
    typeof table.id === 'string' &&
    Array.isArray(table.columns) &&
    Array.isArray(table.dataSource)
  );
};

export const ChangeAnalysisAiTable: React.FC<IChangeAnalysisAiTableProps & PropsWithChildren> = (props) => {
  const {
    className,
    style,
    config,
    children,
    changeData: changeDataProps,
    ...restProps
  } = props || {};

  const {
    titleEn,
    titleCn,
    downloadFileNameEn,
    downloadFileNameCn,
    aiConfig = [],
    alignmentSwitchConfig,
    trendTableColumnSwitch,
  } = config || {};

  const { data } = restProps || {};
  const { isEN } = useLocal();
  const defaultAlignmentValue =
    alignmentSwitchConfig?.defaultValue ?? 'version_aligned';
  const [alignmentValue, setAlignmentValue] = useState<string>(
    defaultAlignmentValue,
  );

  const { data: aiData, loading: aiLoading } = useMultiAiSummary({
    data: data as Record<string, IAiTableData | undefined>,
    aiConfig,
    alignmentValue,
  });

  const [registeredTables, setRegisteredTables] = useState<IRegisteredTableInfo[]>([]);

  const changeData = useCallback((name: string, value: unknown) => {
    if (name === '__registerTable__') {
      if (!isRegisteredTableInfo(value)) {
        return;
      }
      setRegisteredTables((prev) => {
        const existing = prev.find((t) => t.id === value.id);
        if (
          existing &&
          existing.titleEn === value.titleEn &&
          existing.titleCn === value.titleCn &&
          existing.columns === value.columns &&
          existing.dataSource === value.dataSource
        ) {
          return prev;
        }
        const filtered = prev.filter((t) => t.id !== value.id);
        return [...filtered, value];
      });
    } else if (name === '__unregisterTable__') {
      if (typeof value !== 'string') {
        return;
      }
      setRegisteredTables((prev) => {
        if (!prev.some((t) => t.id === value)) {
          return prev;
        }
        return prev.filter((t) => t.id !== value);
      });
    } else {
      changeDataProps?.(name, value);
    }
  }, [changeDataProps]);

  const alignmentOptions = useMemo(() => {
    if (!alignmentSwitchConfig?.options?.length) {
      return [];
    }
    return alignmentSwitchConfig.options.map((item) => ({
      value: item.value,
      label: getName(isEN, item.name, item.nameCn),
    }));
  }, [alignmentSwitchConfig?.options, isEN]);

  const alignmentRadio = useMemo(() => {
    if (!alignmentOptions.length) {
      return null;
    }
    return (
      <Radio.Group
        className="dbd-change-analysis-ai-table-title-alignment"
        value={alignmentValue}
        onChange={(val: string) => {
          if (val !== alignmentValue) {
            setAlignmentValue(val);
          }
        }}
      >
        {alignmentOptions.map((opt) => (
          <Radio key={opt.value} name={opt.value}>
            {opt.label}
          </Radio>
        ))}
      </Radio.Group>
    );
  }, [alignmentOptions, alignmentValue]);

  const registeredTablesHasValidData = useMemo(() => {
    if (!registeredTables || registeredTables.length === 0) {
      return false;
    }

    return registeredTables.some((table) => {
      const dataSource = table.dataSource || [];
      return Array.isArray(dataSource) && dataSource.length > 0;
    });
  }, [registeredTables]);

  const handleDownloadAllTables = useCallback(async () => {
    if (!registeredTablesHasValidData) {
      return;
    }

    try {
      const childrenArray = React.Children.toArray(children);

      const sortedTables = childrenArray
        .map((child) => {
          if (!isValidElement<IChangeAnalysisAiTableChildProps>(child)) {
            return undefined;
          }
          const childTitleEn =
            child.props?.config?.props?.metricsCardWrapProps?.titleEn;
          const childTitleCn =
            child.props?.config?.props?.metricsCardWrapProps?.titleCn;
          return registeredTables.find(
            (table) =>
              (childTitleEn && table.titleEn === childTitleEn) ||
              (childTitleCn && table.titleCn === childTitleCn),
          );
        })
        .filter(Boolean);

      const downloadConfig = {
        fileName: (isEN ? downloadFileNameEn : downloadFileNameCn) || '',
        tabs: sortedTables.map((table) => {
          const columns = table.columns || [];
          const dataSource = table.dataSource || [];
          const tabName =
            getName(isEN, table.titleEn, table.titleCn) || table.id || 'Sheet';

          return convertCommonTableToDownloadConfig(
            dataSource,
            columns,
            tabName,
            isEN,
          );
        }),
      };

      await downloadExcel(downloadConfig);
    } catch (error) {
      console.error('下载失败:', error);
    }
  }, [
    registeredTablesHasValidData,
    children,
    registeredTables,
    isEN,
    downloadFileNameEn,
    downloadFileNameCn,
  ]);

  const downloadOperation = useMemo(() => {
    if (!registeredTablesHasValidData) {
      return null;
    }

    return (
      <div className="dbd-common-table__metrics-card-wrap__operation dbd-common-table__operation-btn">
        <DownloadButton onClick={handleDownloadAllTables} />
      </div>
    );
  }, [registeredTablesHasValidData, handleDownloadAllTables]);

  const titleText = useMemo(
    () => getName(isEN, titleEn, titleCn),
    [isEN, titleEn, titleCn],
  );

  const cardTitle = useMemo(() => {
    if (!alignmentRadio) {
      return titleText;
    }
    return (
      <>
        <span className="dbd-change-analysis-ai-table-title-label">
          {titleText}
        </span>
        {alignmentRadio}
      </>
    );
  }, [titleText, alignmentRadio]);

  /** 从 pageConfig 读取基准列，仅 alignment 变化时重算，避免每轮 render 新 columns 引用触发注册死循环 */
  const trendTableColumns = useMemo(() => {
    if (!trendTableColumnSwitch) {
      return undefined;
    }
    let baseColumns: unknown[] | undefined;
    React.Children.forEach(children, (child) => {
      if (!isValidElement(child)) {
        return;
      }
      const childProps = (
        child as React.ReactElement<IChangeAnalysisAiTableChildProps>
      ).props?.config?.props;
      if (childProps?.dataKey === trendTableColumnSwitch.dataKey) {
        baseColumns = (
          childProps.commonBaseTableProps as { columns?: unknown[]; } | undefined
        )?.columns;
      }
    });
    return applyTrendTableYoyColumn(
      baseColumns,
      trendTableColumnSwitch,
      alignmentValue,
    );
  }, [children, trendTableColumnSwitch, alignmentValue]);

  const renderedChildren = useMemo(() => {
    return React.Children.map(children, (child: ReactNode) => {
      if (!isValidElement<IChangeAnalysisAiTableChildProps>(child)) {
        return child;
      }

      const childConfig = child.props?.config;
      if (!childConfig?.props) {
        return React.cloneElement(child, {
          ...restProps,
          changeData,
        });
      }

      const isTrendTable =
        trendTableColumnSwitch &&
        childConfig.props.dataKey === trendTableColumnSwitch.dataKey &&
        trendTableColumns;

      if (!isTrendTable) {
        return React.cloneElement(child, {
          ...restProps,
          changeData,
          config: childConfig,
        });
      }

      const commonBaseTableProps = childConfig.props.commonBaseTableProps as
        | Record<string, unknown>
        | undefined;

      return React.cloneElement(child, {
        ...restProps,
        changeData,
        config: {
          ...childConfig,
          props: {
            ...childConfig.props,
            commonBaseTableProps: {
              ...commonBaseTableProps,
              columns: trendTableColumns,
            },
          },
        },
      });
    });
  }, [
    children,
    changeData,
    restProps,
    trendTableColumnSwitch,
    trendTableColumns,
  ]);

  return (
    <div
      className={classNames('dbd-change-analysis-ai-table', className)}
      style={style}
    >
      <MetricsCardWrap
        titleEn={cardTitle}
        titleCn={cardTitle}
        className={classNames('dbd-common-table__metrics-card-wrap', className)}
        style={style}
        operation={downloadOperation}
      >
        <ChangeAnalysisAiSummarySection data={aiData} loading={aiLoading} />
        {renderedChildren}
      </MetricsCardWrap>
    </div>
  );
};
